import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { apiClient } from '../services/api';
import { getCustomerId, getUserAttribute } from '../utils/authUser';
import { getMarketplaceEligibilityError } from '../utils/marketplaceEligibility';

const TERMINAL_INSTANCE_STATUSES = new Set(['terminated', 'failed']);

const isNonTerminalInstance = (instance) => {
  const status = (instance?.status || '').toLowerCase();
  return !TERMINAL_INSTANCE_STATUSES.has(status);
};

function Dashboard() {
  const { user, activeCustomerId } = useContext(AuthContext);
  const [stats, setStats] = useState({
    totalInstances: 0,
    runningInstances: 0,
    currentTier: 'starter',
    monthlyUsage: 0
  });
  const [recentInstances, setRecentInstances] = useState([]);
  const [allInstances, setAllInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marketplaceEligibilityError, setMarketplaceEligibilityError] = useState('');

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, [user, activeCustomerId]);

  const fetchDashboardData = async () => {
    try {
      // Fetch instances
      const customerId = activeCustomerId || getCustomerId(user);
      if (!customerId) {
        setAllInstances([]);
        setRecentInstances([]);
        setMarketplaceEligibilityError('Customer ID not found. Please contact support.');
        setStats({
          totalInstances: 0,
          runningInstances: 0,
          currentTier: 'starter',
          monthlyUsage: 0
        });
        return;
      }

      const [instancesResponse, customerResponse] = await Promise.all([
        apiClient.get(`/instances?customer_id=${customerId}`),
        apiClient.get(`/customers/${customerId}`).catch(() => ({ data: {} }))
      ]);
      const instances = instancesResponse.data.instances || [];
      setAllInstances(instances);
      const currentTier = customerResponse.data?.tier || getUserAttribute(user, 'custom:tier') || 'starter';
      setMarketplaceEligibilityError(getMarketplaceEligibilityError(customerResponse.data));

      setStats({
        totalInstances: instances.length,
        runningInstances: instances.filter(i => i.status === 'running').length,
        currentTier,
        monthlyUsage: instances.length * 720 // Approximate hours per month
      });

      setRecentInstances(instances.slice(0, 5));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setMarketplaceEligibilityError('Unable to validate AWS Marketplace agreement for this customer.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      running: 'badge-success',
      provisioning: 'badge-info',
      stopped: 'badge-warning',
      failed: 'badge-danger',
      terminated: 'badge-danger'
    };
    return statusClasses[status] || 'badge-info';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <div className="loading-spinner" style={{ borderTopColor: 'var(--primary-color)' }}></div>
      </div>
    );
  }

  const activeInstance = allInstances.find(isNonTerminalInstance) || null;
  const canCreateInstance = !activeInstance && !marketplaceEligibilityError;

  return (
    <div>
      <div className="page-header">
        <h1>Dashboard</h1>
        {canCreateInstance ? (
          <Link to="/instances/new" className="btn btn-primary">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Instance
          </Link>
        ) : (
          activeInstance ? (
            <Link to={`/instances/${activeInstance.instance_id}`} className="btn btn-secondary">
              View Current Instance
            </Link>
          ) : (
            <span className="btn btn-secondary" style={{ opacity: 0.65, cursor: 'not-allowed' }}>
              Marketplace Agreement Required
            </span>
          )
        )}
      </div>

      {!canCreateInstance && (
        activeInstance ? (
          <div className="card" style={{ marginBottom: '16px' }}>
            Single-instance limit active. Current instance:{' '}
            <Link to={`/instances/${activeInstance.instance_id}`} style={{ color: 'var(--primary-color)' }}>
              {activeInstance.project_name || activeInstance.instance_id}
            </Link>{' '}
            ({activeInstance.status}).
          </div>
        ) : marketplaceEligibilityError ? (
          <div className="card" style={{ marginBottom: '16px' }}>
            {marketplaceEligibilityError}
          </div>
        ) : null
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Instances</h3>
          <div className="stat-value">{stats.totalInstances}</div>
        </div>
        <div className="stat-card">
          <h3>Running Instances</h3>
          <div className="stat-value">{stats.runningInstances}</div>
          <div className="stat-change positive">
            {stats.runningInstances > 0 ? 'Active' : 'No active instances'}
          </div>
        </div>
        <div className="stat-card">
          <h3>Current Plan</h3>
          <div className="stat-value" style={{ textTransform: 'capitalize' }}>{stats.currentTier}</div>
        </div>
        <div className="stat-card">
          <h3>Usage This Month</h3>
          <div className="stat-value">{stats.monthlyUsage}h</div>
          <div className="stat-change">Instance hours</div>
        </div>
      </div>

      {/* Recent Instances */}
      <div className="card">
        <div className="card-header">
          <h2>Recent Instances</h2>
          <Link to="/instances" className="btn btn-secondary">View All</Link>
        </div>

        {recentInstances.length === 0 ? (
          <div className="empty-state">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
              <rect x="2" y="3" width="20" height="14" rx="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
            <h3>No instances yet</h3>
            <p>
              {marketplaceEligibilityError
                ? marketplaceEligibilityError
                : 'Create your first Virtuoso instance to get started'}
            </p>
            {canCreateInstance && (
              <Link to="/instances/new" className="btn btn-primary">Create Instance</Link>
            )}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Tier</th>
                <th>Region</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {recentInstances.map(instance => (
                <tr key={instance.instance_id}>
                  <td>
                    <Link to={`/instances/${instance.instance_id}`} style={{ color: 'var(--primary-color)', textDecoration: 'none', fontWeight: 500 }}>
                      {instance.project_name || instance.instance_id}
                    </Link>
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadge(instance.status)}`}>
                      {instance.status}
                    </span>
                  </td>
                  <td style={{ textTransform: 'capitalize' }}>{instance.tier}</td>
                  <td>{instance.region}</td>
                  <td>{new Date(instance.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Quick Links */}
      <div className="stats-grid">
        <div className="card" style={{ cursor: 'pointer' }} onClick={() => window.open('https://docs.openlinksw.com/virtuoso/', '_blank')}>
          <h3 style={{ marginBottom: '8px' }}>Documentation</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Learn how to use Virtuoso</p>
        </div>
        <div className="card" style={{ cursor: 'pointer' }} onClick={() => window.open('https://community.openlinksw.com/', '_blank')}>
          <h3 style={{ marginBottom: '8px' }}>Community</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Get help from the community</p>
        </div>
        <div className="card" style={{ cursor: 'pointer' }}>
          <h3 style={{ marginBottom: '8px' }}>Support</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Contact our support team</p>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
